/**
 * Muokkaa signaaleja source→target row model, split out so bun:test can cover
 * it without a DOM (same pattern as signal-tree-expand.ts / activity-feed-filters.ts).
 *
 * Source → target editor: left is the raw taught column (lähdesignaali), right
 * picks the KB Signaali. Per-row kytketty / ei kytketty is draft until Tallenna;
 * only wired rows with a target enter `PUT .../signal-map`. Turning a row
 * off omits it from the active map but the chosen Signaali stays as a
 * preferred pair (reload via `targets_from_preferred`).
 */

/** Structural row shape — only the fields classification/filtering need, so
 *  this module stays free of React/API-client deps. `MuokkaaSignalRow` (the
 *  dialog's prop type) satisfies this. */
export interface SignalMapRow {
  id: string;
  /** Raw CSV / vendor column when taught via csv_signal_schemas; absent/empty
   *  means this KB id has no taught source yet. */
  source_signal?: string | null;
  nature:
    | "sensor"
    | "setpoint"
    | "actuator"
    | "status"
    | "calculated"
    | "forecast"
    | "marker";
  /** Non-null when this id's value is fed from another equipment/zone
   *  (a "syöte" / upstream connection) rather than taught locally. */
  connection_source: string | null;
}

export type SignalMapFilter = "all" | "mapped" | "unmapped";

export interface PartitionedSignalRows<T extends SignalMapRow> {
  /** Editable source → target pairs, after the mapped/unmapped filter. */
  pairRows: T[];
  /** Read-only: no taught source, identity inferred (nature === "calculated"). */
  derivedRows: T[];
  /** Read-only: no taught source, value fed from another equipment/zone. */
  upstreamRows: T[];
}

function has_taught_source(row: SignalMapRow): boolean {
  return !!row.source_signal?.trim();
}

/** Filter an already-classified pair list by mapped/unmapped. Split out from
 *  `partition_signal_rows` so the component can re-run the (cheap) filter on
 *  a filter change without re-running the (linear) classification pass. */
export function filter_pair_rows<T extends SignalMapRow>(
  pairRows: readonly T[],
  filter: SignalMapFilter,
): T[] {
  if (filter === "all") return [...pairRows];
  return pairRows.filter((row) =>
    filter === "mapped" ? has_taught_source(row) : !has_taught_source(row),
  );
}

/**
 * The map editor lists taught source → target pairs only.
 *
 * A row with no lähdesignaali is not a pair — it used to render as "—" with a
 * Signaali control that Tallenna silently dropped. Calculated / syöte rows
 * without a taught source are classified but the dialog does not show them
 * (they belong on the Signaalit list). A taught source on a calculated or
 * upstream id is still a genuine editable pair.
 */
export function partition_signal_rows<T extends SignalMapRow>(
  rows: readonly T[],
  filter: SignalMapFilter,
): PartitionedSignalRows<T> {
  const pairRows: T[] = [];
  const derivedRows: T[] = [];
  const upstreamRows: T[] = [];

  for (const row of rows) {
    if (has_taught_source(row)) {
      pairRows.push(row);
      continue;
    }
    if (row.connection_source) {
      upstreamRows.push(row);
    } else if (row.nature === "calculated") {
      derivedRows.push(row);
    }
  }

  return {
    pairRows: filter_pair_rows(pairRows, filter),
    derivedRows,
    upstreamRows,
  };
}

/** In-dialog lähdesignaali that is not yet in the taught map. Tallenna
 *  omits it until the operator picks a KB target. */
export const DRAFT_SOURCE_ROW_PREFIX = "__draft:";

export function is_draft_source_row_id(id: string): boolean {
  return id.startsWith(DRAFT_SOURCE_ROW_PREFIX);
}

export function next_draft_source_row_id(
  existingIds: readonly string[],
): string {
  const used = new Set(existingIds);
  let n = 1;
  while (used.has(`${DRAFT_SOURCE_ROW_PREFIX}${n}`)) n += 1;
  return `${DRAFT_SOURCE_ROW_PREFIX}${n}`;
}

/** Unmapped Lähdesignaali names → draft pair rows (ei kytketty until switched on). */
export function unmapped_to_draft_rows(
  unmappedNames: readonly string[],
  alreadyListed: readonly { source_signal?: string | null; id: string }[],
): { rows: SignalMapRow[]; wiredIds: string[] } {
  const rows: SignalMapRow[] = [];
  const wiredIds: string[] = [];
  let ids = alreadyListed.map((row) => row.id);
  for (const raw of unmappedNames) {
    const name = raw.trim();
    if (!name || source_already_listed([...alreadyListed, ...rows], name)) {
      continue;
    }
    const id = next_draft_source_row_id(ids);
    ids = [...ids, id];
    rows.push({
      id,
      nature: "sensor",
      connection_source: null,
      source_signal: name,
    });
    // Unmapped = ei kytketty — do not add to wiredIds.
  }
  return { rows, wiredIds };
}

/**
 * Lähdesignaali → Signaali for every pair that still has both ends — including
 * ei kytketty rows. Off only drops receiving from the active map; the chosen
 * Signaali is kept so reopen can show it again.
 */
export function preferred_source_targets<T extends SignalMapRow>(
  pairRows: readonly T[],
  targets: Readonly<Record<string, string>>,
): Record<string, string> {
  const preferred: Record<string, string> = {};
  for (const row of pairRows) {
    const source = row.source_signal?.trim();
    if (!source) continue;
    const target = map_row_target(row, targets);
    if (!target) continue;
    preferred[source] = target;
  }
  return preferred;
}

/** Apply preferred lähdesignaali→Signaali onto draft/taught row ids. */
export function targets_from_preferred(
  rows: readonly { id: string; source_signal?: string | null }[],
  preferred: Readonly<Record<string, string>>,
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const row of rows) {
    const source = row.source_signal?.trim();
    if (!source) continue;
    const target = preferred[source]?.trim();
    if (!target) continue;
    next[row.id] = target;
  }
  return next;
}

/** KB matcher rows → lähdesignaali → data_point_id (highest confidence wins). */
export function preferred_from_suggestions(
  suggestions: readonly {
    bms_name: string;
    data_point_id: string;
    confidence?: number;
  }[],
): Record<string, string> {
  const best = new Map<string, { target: string; confidence: number }>();
  for (const suggestion of suggestions) {
    const source = suggestion.bms_name.trim();
    const target = suggestion.data_point_id.trim();
    if (!source || !target) continue;
    const confidence = suggestion.confidence ?? 0;
    const prev = best.get(source);
    if (!prev || confidence > prev.confidence) {
      best.set(source, { target, confidence });
    }
  }
  const out: Record<string, string> = {};
  for (const [source, { target }] of best) out[source] = target;
  return out;
}

/**
 * Signaali shown for every pair row — preferred when remembered, else the
 * taught canonical id. Drafts with no preferred stay empty until a KB
 * candidate suggest or operator pick fills them. Used so off/on both show
 * the suggestion instead of a blank Select.
 */
export function seed_pair_targets(
  rows: readonly SignalMapRow[],
  preferred: Readonly<Record<string, string>> = {},
): Record<string, string> {
  const next = targets_from_preferred(rows, preferred);
  for (const row of rows) {
    if (next[row.id]) continue;
    if (is_draft_source_row_id(row.id)) continue;
    next[row.id] = row.id;
  }
  return next;
}

function normalize_match_token(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9äöå]/gi, "");
}

/**
 * Best-effort Signaali ehdotus from the KB candidate list when no preferred
 * mapping is remembered. One candidate per source; already-claimed targets
 * are skipped.
 */
export function suggest_targets_from_candidates(
  sources: readonly string[],
  candidates: readonly { id: string; name_fi: string }[],
  claimedTargets: ReadonlySet<string> = new Set(),
): Record<string, string> {
  const available = candidates.filter((c) => !claimedTargets.has(c.id));
  if (available.length === 0) return {};

  const out: Record<string, string> = {};
  const used = new Set<string>();

  for (const raw of sources) {
    const source = raw.trim();
    if (!source) continue;
    const sourceNorm = normalize_match_token(source);
    if (!sourceNorm) continue;

    let bestId: string | null = null;
    let bestScore = 0;
    for (const candidate of available) {
      if (used.has(candidate.id)) continue;
      const idNorm = normalize_match_token(candidate.id);
      const nameNorm = normalize_match_token(candidate.name_fi);
      let score = 0;
      if (idNorm && (sourceNorm.includes(idNorm) || idNorm.includes(sourceNorm))) {
        score = Math.max(score, 3);
      }
      if (
        nameNorm &&
        nameNorm.length >= 4 &&
        (sourceNorm.includes(nameNorm) || nameNorm.includes(sourceNorm))
      ) {
        score = Math.max(score, 2);
      }
      if (score > bestScore) {
        bestScore = score;
        bestId = candidate.id;
      }
    }
    if (bestId && bestScore > 0) {
      out[source] = bestId;
      used.add(bestId);
    }
  }
  return out;
}

const PREFERRED_STORAGE_PREFIX = "kairos.signaalit.preferred.";

/** Session-backed preferred pairs so off→on survives refresh/remount. */
export function read_preferred_source_targets(
  equipmentId: string,
): Record<string, string> {
  if (typeof sessionStorage === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(PREFERRED_STORAGE_PREFIX + equipmentId);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(
      parsed as Record<string, unknown>,
    )) {
      if (typeof value !== "string") continue;
      const source = key.trim();
      const target = value.trim();
      if (source && target) out[source] = target;
    }
    return out;
  } catch {
    return {};
  }
}

export function write_preferred_source_targets(
  equipmentId: string,
  preferred: Readonly<Record<string, string>>,
): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      PREFERRED_STORAGE_PREFIX + equipmentId,
      JSON.stringify(preferred),
    );
  } catch {
    /* private mode / quota */
  }
}

/** Wired rows that still have no Signaali — Tallenna must not silently drop them. */
export function wired_rows_missing_signaali<T extends SignalMapRow>(
  pairRows: readonly T[],
  targets: Readonly<Record<string, string>>,
  wiredRowIds: ReadonlySet<string>,
): T[] {
  return pairRows.filter(
    (row) => wiredRowIds.has(row.id) && !map_row_target(row, targets),
  );
}

export function source_already_listed(
  rows: readonly { source_signal?: string | null }[],
  name: string,
): boolean {
  const normalized = name.trim();
  if (!normalized) return true;
  return rows.some((row) => (row.source_signal ?? "").trim() === normalized);
}

/** The KB id a row currently targets, or null when a typed lähdesignaali
 *  has no Signaali yet (must not be PUT). */
export function map_row_target(
  row: Pick<SignalMapRow, "id">,
  targets: Readonly<Record<string, string>>,
): string | null {
  const chosen = targets[row.id];
  if (chosen) return chosen;
  if (is_draft_source_row_id(row.id)) return null;
  return row.id;
}

/** Target ids already claimed by some *wired* pair row's current selection —
 *  used to disable a target on every *other* row's control so a retarget can't
 *  silently duplicate a target already in use. Unwired rows do not claim.
 */
export function claimed_target_ids<T extends SignalMapRow>(
  pairRows: readonly T[],
  targets: Readonly<Record<string, string>>,
  wiredRowIds?: ReadonlySet<string>,
): Set<string> {
  const claimed = new Set<string>();
  for (const row of pairRows) {
    if (wiredRowIds && !wiredRowIds.has(row.id)) continue;
    const target = map_row_target(row, targets);
    if (target) claimed.add(target);
  }
  return claimed;
}

/**
 * The draft `signal_map` body for `PUT .../signal-map` (#1536): taught
 * source → target pairs that are still kytketty. A pair row with no taught
 * `source_signal` has no lähdesignaali to key on, so it is excluded — the map
 * is a rewrite of the taught pairing, not a declare list. When `wiredRowIds`
 * is set, only those row ids are included (ei kytketty → omitted on Tallenna).
 */
export function build_signal_map<T extends SignalMapRow>(
  pairRows: readonly T[],
  targets: Readonly<Record<string, string>>,
  wiredRowIds?: ReadonlySet<string>,
): Record<string, string> {
  const signalMap: Record<string, string> = {};
  for (const row of pairRows) {
    if (wiredRowIds && !wiredRowIds.has(row.id)) continue;
    const source = row.source_signal?.trim();
    if (!source) continue;
    const target = map_row_target(row, targets);
    if (!target) continue;
    signalMap[source] = target;
  }
  return signalMap;
}

// ── Effective source→target resolution (#1519 changes-requested) ───────────
//
// The dialog's Lähdesignaali column used to be built from Building's
// csv_signal_schemas alone, while `PUT .../signal-map` (write_signal_map,
// services/building) rewrites whichever store the equipment actually uses:
// Device's connector signal_schema when a live connector exists, else the CSV
// map. Reading only the CSV half meant a connector-fed equipment with no CSV
// row opened every field with source_signal: null — Tallenna then PUT an
// empty map and wiped the connector's taught schema. `resolve_source_signal_by_source`
// applies the same store choice on read that the write path applies, so
// Muokkaa never shows (or silently replaces) a pairing it cannot retarget.

/** Structural shape of one taught raw→canonical map — a Building CSV
 *  `csv_signal_schemas` row or a Device connector's `signal_schema`. */
export interface RawSignalSchema {
  equipment_id?: string | null;
  zone_id?: string | null;
  signal_schema: Readonly<Record<string, string>>;
}

/** canonical id → raw Lähdesignaali. First raw wins if several columns map to
 *  the same canonical id. */
function invert_signal_schema(
  schema: Readonly<Record<string, string>>,
): Record<string, string> {
  const inverted: Record<string, string> = {};
  for (const [raw, canonical] of Object.entries(schema)) {
    if (!inverted[canonical]) inverted[canonical] = raw;
  }
  return inverted;
}

/**
 * source_id (equipment or zone) → canonical id → raw Lähdesignaali.
 *
 * A source with a live-connector entry reads *only* that connector's map —
 * even when it is empty, which is a real "nothing taught yet" state, not a
 * reason to fall back to a possibly-stale (or simply absent) CSV row. Every
 * other source reads the Building CSV file-import map, unchanged from before.
 * `connectorSchemas` is keyed by `equipment_id` only (a connector never feeds
 * a zone), and is applied second so its entries always win on overlap — the
 * caller passes both lists as-fetched, with no equipment_id → schema dict to
 * pre-build.
 */
export function resolve_source_signal_by_source(
  csvSchemas: readonly RawSignalSchema[],
  connectorSchemas: readonly RawSignalSchema[],
): Record<string, Record<string, string>> {
  const map: Record<string, Record<string, string>> = {};
  for (const schema of csvSchemas) {
    const sourceId = schema.equipment_id ?? schema.zone_id;
    if (sourceId) map[sourceId] = invert_signal_schema(schema.signal_schema);
  }
  for (const schema of connectorSchemas) {
    if (schema.equipment_id) {
      map[schema.equipment_id] = invert_signal_schema(schema.signal_schema);
    }
  }
  return map;
}

// ── Row set: taught map ∪ observed history (#1519 changes-requested round 2) ─
//
// The dialog's row set used to come from `available_names` alone (observed
// data-point history) with the taught map only consulted per row that
// already existed. A taught pair whose canonical id has no history yet — a
// live connector never sampled, or a stopped feed — was therefore invisible,
// excluded from `build_signal_map`, and silently dropped by Tallenna's
// whole-map replace on a completely unedited save. `build_muokkaa_rows`
// unions the two so every pair the write path would touch is rendered and
// round-trips.

/** One observed row from `DataPointSource.available_names` — the fields
 *  `build_muokkaa_rows` needs, kept structural like `RawSignalSchema`. */
export interface ObservedSignalName {
  name: string;
  nature: SignalMapRow["nature"];
  connection_source: string | null;
}

/**
 * Union rows built from observed history with canonical ids that exist only
 * in the taught map. Taught-only rows (no history sample yet) default to
 * `nature: "sensor"` / `connection_source: null` — both are irrelevant for
 * them because a taught `source_signal` makes `partition_signal_rows`
 * classify the row as an editable pair immediately, before either field is
 * ever consulted (see `has_taught_source`).
 */
export function build_muokkaa_rows(
  observed: readonly ObservedSignalName[],
  sourceSignalByCanonicalId: Readonly<Record<string, string>>,
): SignalMapRow[] {
  const rows: SignalMapRow[] = observed.map((n) => ({
    id: n.name,
    nature: n.nature,
    connection_source: n.connection_source,
    source_signal: sourceSignalByCanonicalId[n.name] ?? null,
  }));
  const observedIds = new Set(rows.map((r) => r.id));
  for (const [canonicalId, sourceSignal] of Object.entries(
    sourceSignalByCanonicalId,
  )) {
    if (observedIds.has(canonicalId)) continue;
    rows.push({
      id: canonicalId,
      nature: "sensor",
      connection_source: null,
      source_signal: sourceSignal,
    });
  }
  return rows;
}
